import { useEffect, useState } from "react";
import { Truck, Plus, CheckCircle2, Ban, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { fmt, today } from "../utils";
import { useLang } from "../i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const statusColor = {
  "Pending Generation": "bg-amber-50 text-amber-700",
  Generated: "bg-emerald-50 text-emerald-700",
  Cancelled: "bg-rose-50 text-rose-700",
  Expired: "bg-slate-100 text-slate-500",
};
const emptyForm = { sale_id: "", challan_id: "", transport_mode: "Road", transporter_name: "", transporter_id: "", vehicle_no: "", distance_km: "" };

export default function EWayBills() {
  const [rows, setRows] = useState([]);
  const [sales, setSales] = useState([]);
  const [challans, setChallans] = useState([]);
  const [open, setOpen] = useState(false);
  const [ewbModal, setEwbModal] = useState(null);
  const [ewbNo, setEwbNo] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [gateway, setGateway] = useState({ configured: false });
  const { t } = useLang();

  const load = async () => {
    const [ewb, saleRows, challanRows, gatewayStatus] = await Promise.all([
      api.get("/ewaybills"),
      api.get("/sales"),
      api.get("/challans"),
      api.get("/compliance/gateway").catch(() => ({ data: { configured: false } })),
    ]);
    setRows(ewb.data);
    setSales(saleRows.data);
    setChallans(challanRows.data);
    setGateway(gatewayStatus.data || { configured: false });
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const create = async () => {
    if (!form.sale_id && !form.challan_id) return toast.error("Select a Sales Bill or Delivery Challan");
    try {
      await api.post("/ewaybills", { ...form, distance_km: Number(form.distance_km) || 0 });
      toast.success(gateway.configured ? "E-way entry ready — API se generate kar sakte ho" : "E-way entry ready — NIC bulk JSON/manual flow available");
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(err.message); }
  };

  const markGenerated = async () => {
    if (!ewbNo.trim()) return toast.error("EWB No. daalo");
    try {
      await api.put(`/ewaybills/${ewbModal.id}`, { ewb_no: ewbNo.trim(), ewb_date: today() });
      toast.success("E-way bill Generated mark ho gaya");
      setEwbModal(null); setEwbNo(""); load();
    } catch (err) { toast.error(err.message); }
  };

  const generateViaApi = async (e) => {
    try {
      await api.post(`/compliance/eway/${e.id}/generate`);
      toast.success("E-Way Bill generated through configured gateway");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const cancelEwb = async (e) => {
    if (!window.confirm(`Cancel E-Way Bill ${e.ewb_no || e.invoice_no}?`)) return;
    try {
      if (e.gateway_generated_at && e.ewb_no) {
        await api.post(`/compliance/eway/${e.id}/cancel`, { reason_code: "1", reason: "Cancelled by user in SGP Office OS" });
      } else {
        await api.put(`/ewaybills/${e.id}`, { status: "Cancelled" });
      }
      toast.success("E-way bill cancelled");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const downloadJson = async () => {
    const r = await api.get("/ewaybills/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
    const a = document.createElement("a"); a.href = url; a.download = "ewaybill_bulk_upload.json"; a.click(); URL.revokeObjectURL(url);
    toast.success("NIC bulk-upload JSON download — ewaybillgst.gov.in pe upload karo");
  };

  const sale = sales.find((s) => s.id === form.sale_id);
  const pendingCount = rows.filter((r) => r.status === "Pending Generation").length;

  return (
    <div data-testid="eway-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("eway")}</h1>
          <p className="sgp-page-subtitle">NIC bulk JSON/manual fallback + configured authorised gateway se direct generation/cancellation.</p>
          <p className={`mt-1 text-[11px] font-semibold ${gateway.configured ? "text-emerald-700" : "text-amber-700"}`}>
            Gateway: {gateway.configured ? `${gateway.provider} · ${gateway.environment}` : "Not configured — existing portal workflow remains available"}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="eway-export-btn" onClick={downloadJson}
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md border border-slate-300 hover:border-teal-600 hover:text-teal-700">
            <Download className="h-4 w-4" /> NIC Bulk JSON {pendingCount > 0 && `(${pendingCount})`}
          </button>
          <button data-testid="add-eway-btn" onClick={() => setOpen(true)}
            className="sgp-primary-btn">
            <Plus className="h-4 w-4" /> {t("add")}
          </button>
        </div>
      </div>

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[1000px]">
          <thead>
            <tr>
              <th className="sgp-th">Invoice</th><th className="sgp-th">Date</th><th className="sgp-th">Hospital</th>
              <th className="sgp-th text-right">Value</th><th className="sgp-th">Vehicle</th>
              <th className="sgp-th text-right">Distance</th><th className="sgp-th">EWB No.</th>
              <th className="sgp-th">Valid Till</th><th className="sgp-th">Status</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody data-testid="eway-table">
            {rows.map((e) => (
              <tr key={e.id} data-testid={`eway-row-${e.id}`} className="sgp-table-row">
                <td className="sgp-td font-mono text-xs font-semibold">{e.invoice_no}</td>
                <td className="sgp-td font-mono text-xs">{e.date}</td>
                <td className="sgp-td">{e.hospital_name}</td>
                <td className="sgp-td text-right font-mono text-xs">{fmt(e.grand_total)}</td>
                <td className="sgp-td font-mono text-xs">{e.vehicle_no || "-"}</td>
                <td className="sgp-td text-right font-mono text-xs">{e.distance_km ? `${e.distance_km} km` : "-"}</td>
                <td className="sgp-td font-mono text-xs">{e.ewb_no || "-"}</td>
                <td className="sgp-td font-mono text-xs">{e.valid_till || "-"}</td>
                <td className="sgp-td"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusColor[e.status]}`}>{e.status}</span></td>
                <td className="sgp-td">
                  <div className="flex gap-1">
                    {e.status === "Pending Generation" && gateway.configured && e.sale_id && (
                      <button data-testid={`eway-api-${e.id}`} title="Generate through configured API gateway" onClick={() => generateViaApi(e)}
                        className="px-2 py-1 rounded bg-emerald-50 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100">API Generate</button>
                    )}
                    {e.status === "Pending Generation" && (
                      <button data-testid={`eway-mark-${e.id}`} title="Enter EWB No. from NIC portal" onClick={() => setEwbModal(e)}
                        className="p-1.5 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-700"><CheckCircle2 className="h-4 w-4" /></button>
                    )}
                    {e.status !== "Cancelled" && (
                      <button data-testid={`eway-cancel-${e.id}`} title="Cancel" onClick={() => cancelEwb(e)}
                        className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600"><Ban className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={10} className="sgp-td text-center py-10 text-slate-400">
                <Truck className="h-6 w-6 mx-auto mb-2 text-slate-300" />Abhi koi e-way bill nahi. Sales invoice select karke banao.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="eway-form-modal" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">New E-Way Bill (Part A + B)</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2"><label className="sgp-label">Sales Invoice or Delivery Challan *</label>
              <select data-testid="eway-sale-select" className="sgp-input" value={form.sale_id ? `sale:${form.sale_id}` : form.challan_id ? `challan:${form.challan_id}` : ""} onChange={(e) => { const [kind, id] = e.target.value.split(":"); setForm({ ...form, sale_id: kind === "sale" ? id : "", challan_id: kind === "challan" ? id : "" }); }}>
                <option value="">— Select invoice —</option>
                {sales.map((s) => <option key={s.id} value={`sale:${s.id}`}>{s.bill_no} · {s.hospital_name} · {fmt(s.grand_total)}</option>)}
                {challans.filter(c => c.status === "Dispatched").map(c => <option key={c.id} value={`challan:${c.id}`}>{c.bill_no} · {c.hospital_name} · {fmt(c.grand_total)}</option>)}
              </select>
              {form.challan_id && <p className="text-[10px] text-amber-700 mt-1">For challans, record the EWB number manually after generating it on the government portal. Bulk JSON export supports invoices only.</p>}
              {sale && sale.grand_total < 50000 && <p className="text-[10px] text-amber-600 mt-1">₹50,000 se kam — e-way bill optional hai</p>}
            </div>
            <div><label className="sgp-label">Transport Mode</label>
              <select data-testid="eway-mode-input" className="sgp-input" value={form.transport_mode} onChange={(e) => setForm({ ...form, transport_mode: e.target.value })}>
                {["Road", "Rail", "Air", "Ship"].map((m) => <option key={m}>{m}</option>)}
              </select></div>
            <div><label className="sgp-label">Distance (km)</label>
              <input data-testid="eway-distance-input" type="number" className="sgp-input font-mono" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} /></div>
            <div><label className="sgp-label">Vehicle No.</label>
              <input data-testid="eway-vehicle-input" className="sgp-input font-mono" value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })} /></div>
            <div><label className="sgp-label">Transporter Name</label>
              <input data-testid="eway-transporter-input" className="sgp-input" value={form.transporter_name} onChange={(e) => setForm({ ...form, transporter_name: e.target.value })} /></div>
            <div className="col-span-2"><label className="sgp-label">Transporter ID (GSTIN, optional)</label>
              <input data-testid="eway-transporter-id-input" className="sgp-input font-mono" value={form.transporter_id} onChange={(e) => setForm({ ...form, transporter_id: e.target.value })} /></div>
            <button data-testid="eway-save-btn" onClick={create}
              className="col-span-2 py-2 text-sm font-semibold rounded-md bg-teal-700 text-white hover:bg-teal-800">Create E-Way Bill Entry</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ewbModal} onOpenChange={() => setEwbModal(null)}>
        <DialogContent data-testid="ewb-no-modal" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">EWB No. — {ewbModal?.invoice_no}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-2">ewaybillgst.gov.in pe generate karke 12-digit EWB number yahan daalo. Validity auto-calculate hogi (1 day / 200 km).</p>
          <input data-testid="ewb-no-input" className="sgp-input font-mono" placeholder="e.g. 2910 1234 5678" value={ewbNo} onChange={(e) => setEwbNo(e.target.value)} />
          <button data-testid="ewb-confirm-btn" onClick={markGenerated}
            className="w-full mt-3 py-2 text-sm font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700">Mark Generated</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
